import {
  Address,
  CommonMappingData,
  createAddress,
  DataChangeType,
  IMultiProductCard,
  IProduktAuswahlDetails,
  IProduktVerfuegbarkeit,
  IVerknuepfungen,
  IVerknuepfungenProdukte,
  PreisIndikation,
  Product,
  RecentOrder,
  RecentOrderDTO,
  RecentOrderType,
  Sparte,
} from '@mwe/models';
import { parseAddressCode } from '../address/address.utils';
import { containsFernwaerme, formatTariffName, isProductCategoryFernwaerme, sortProductsEnumCategory } from './product-category.utils';
import { isArrayWithMinOneItem } from '../common/mwe-util';

export const parseVerknuepfungen = (associations: IVerknuepfungen[]): Product[] => {
  if (!isArrayWithMinOneItem(associations)) {
    return [];
  }

  return associations.flatMap(el => parseVerknuepfung(el)).filter(i => !!i);
};

export const parseVerknuepfung = (association: IVerknuepfungen, ignoreVerknuepfungsStatus = false): Product[] => {
  if (association.produkte && (association.verknuepfungsStatus !== null || ignoreVerknuepfungsStatus)) {
    return getProductFromAssociation(association.produkte, association.geschaeftsPartnerId, association.systemId);
  }
  return [];
};

export const getProductFromAssociation = (products: IVerknuepfungenProdukte[], customerNumber: string, systemId: string): Product[] => {
  if (!isArrayWithMinOneItem(products)) {
    return [];
  }

  return products
    .map(p => {
      const isFwCategory = isProductCategoryFernwaerme(p.sparte);

      const customerNumberPerCategory = isFwCategory ? p.verrechnungskontoId.slice(2) : customerNumber;
      const tariffClasses = getTariffClassesFromAssociation(p);
      const tarifKey = p.additionalData?.produktDetails?.[0]?.ISUTarifKey;
      const anlageId = p.additionalData?.produktDetails?.[0]?.anlageId;

      return {
        id: null,
        customerNumber: customerNumberPerCategory,
        accountNumber: p.verrechnungskontoId,
        businessPartnerNumber: p.geschaeftsPartnerId,
        category: p.sparte,
        tariffClasses,
        address: parseAdresse(p.additionalData?.adressDetails, isFwCategory),
        vertragsStatus: p.vertragsStatus,
        anlageId,
        tarifKey,
        systemId,
        associationDetails: p.additionalData?.produktDetails,
      } as Product;
    })
    .filter(p => !!p);
};

const getTariffClassesFromAssociation = (product: IVerknuepfungenProdukte): string[] => {
  if (!isArrayWithMinOneItem(product?.additionalData?.produktDetails)) {
    return [];
  }

  const tariffClasses = [];
  product.additionalData.produktDetails.forEach(detail => {
    if (detail?.tarifName) {
      if (detail.tarifName.toLowerCase() === 'heizung_warmwasser') {
        tariffClasses.push(parseTariff('heizung'));
        tariffClasses.push(parseTariff('warmwasser'));
      } else {
        tariffClasses.push(parseTariff(detail.tarifName));
      }
    }
  });

  return tariffClasses;
};

export const parseAdresse = (addressDetails: any, isFwCategory: boolean): Address => {
  if (!addressDetails) {
    return null;
  }

  return createAddress({
    id: null,
    street: addressDetails.strasse,
    streetNumber: addressDetails.hausnummer,
    block: addressDetails.stiege,
    doorNumber: addressDetails.tuernummer,
    hint: isFwCategory ? null : addressDetails?.adresshinweis,
    postcode: addressDetails.plz,
    city: addressDetails.ort,
    country: addressDetails.land,
  });
};

export const parseTariff = (tariff: string) => {
  return tariff ? tariff.replace(/^Strom |^Gas |^Erdgas |^NachtStrom |^Stromrueck /, '') : '';
};

export const getProductsFromCommonMapping = (items: CommonMappingData[]): Product[] => {
  if (!isArrayWithMinOneItem(items)) {
    return [];
  }

  return items.flatMap(i => {
    return i.categories.map(c => {
      return {
        category: c,
        accountNumber: i.accountNumber,
        customerNumber: i.customerNumber,
      } as Product;
    });
  });
};

export const createMultiProductCardsByAccountNumber = (products: Product[]): IMultiProductCard[] => {
  const accountNumbers = [...new Set(products.map(product => product.accountNumber))];

  return accountNumbers.map(accountNumber => {
    const filteredProducts = products.filter(product => product.accountNumber === accountNumber);
    const productCategories = sortProductsEnumCategory(
      filteredProducts.map(product => product.category),
      true,
    );
    const customerNumber = filteredProducts?.[0]?.customerNumber;
    const isFernwaerme = containsFernwaerme(productCategories);
    const displayAccountNumber = isFernwaerme ? accountNumber.slice(2) : accountNumber;
    return {
      id: accountNumber,
      categories: productCategories,
      accountNumber,
      customerNumber,
      displayAccountNumber,
      displayAccountNumberLabel: isFernwaerme ? 'shared.customerNumberLabel' : 'shared.accountNumberLabel',
    };
  });
};

function sortAndDistinctProductCategories(filteredProducts: Product[]) {
  const productCategories = sortProductsEnumCategory(
    filteredProducts.map(product => product.category),
    true,
  );
  return Array.from(new Set(productCategories));
}

export function createMultiProductCardsByBusinessPartnerId(products: Product[]) {
  const businessPartnerIds = Array.from(new Set(products.map(product => product.businessPartnerNumber)));

  return businessPartnerIds.map(businessPartnerId => {
    const productsWithMatchingBusinessPartner = products.filter(product => product.businessPartnerNumber === businessPartnerId);
    const productCategories = sortAndDistinctProductCategories(productsWithMatchingBusinessPartner);

    return {
      id: businessPartnerId,
      categories: productCategories,
      accountNumbers: productsWithMatchingBusinessPartner.map(product => product.accountNumber),
      customerNumbers: productsWithMatchingBusinessPartner.map(product => product.customerNumber),
    };
  });
}

export const parseRecentOrdersDTO = (recentOrdersApi: RecentOrderDTO[]): RecentOrder[] => {
  return recentOrdersApi.map(item => {
    const type = item.type as RecentOrderType;
    const dataType = item.dataType as DataChangeType;
    let msgKey = '';

    switch (type) {
      case RecentOrderType.NewClient:
        msgKey =
          dataType === DataChangeType.NewClientMAC
            ? 'products.recentOrders.nc_mac_workInProgress'
            : 'products.recentOrders.nc_workInProgress';
        break;
      case RecentOrderType.TariffChange:
        msgKey =
          dataType === DataChangeType.TariffChangeMAC ? 'products.recentOrders.tc_mac_workInProgress' : getTariffChangeMessage(dataType);
        break;
      case RecentOrderType.InvoiceDataChange:
        msgKey = getInvoiceDataChangeMessage(dataType);
        break;
      case RecentOrderType.VoucherRedeem:
        msgKey = 'products.recentOrders.vr_workInProgress';
        break;
      case RecentOrderType.ChargingCardOrder:
        msgKey = 'products.recentOrders.cco_workInProgress';
        break;
      case RecentOrderType.PaymentAgreement:
        msgKey = getPaymentAgreementMessage(dataType);
        break;
      case RecentOrderType.PartialAmountChange:
        msgKey = 'products.recentOrders.pac_workInProgress';
        break;
      case RecentOrderType.CAInformationRequest:
        msgKey = 'products.recentOrders.cair_workInProgress';
        break;
      case RecentOrderType.Relocation:
        msgKey = 'products.recentOrders.uz_workInProgress';
        break;
      case RecentOrderType.MoveOut:
        msgKey = 'products.recentOrders.az_workInProgress';
        break;
    }

    // only use addressCode to parse if it entails all information
    const _address = item.address ? createAddress(item.address) : item.addressCode ? parseAddressCode(item.addressCode) : null;
    const customerData = item.customerData;

    // parse companyName for recent orders, to be compatible with CustomerNumberInfoTooltipCardComponent
    if (item.businessData) {
      customerData.geschaeftsPartner = {
        ...customerData?.geschaeftsPartner,
        organisation: {
          ...customerData?.geschaeftsPartner?.organisation,
          organisationsname: item?.businessData?.companyName,
        },
      };
    }

    return {
      id: item.id,
      orderedAt: item.orderedAt,
      type,
      dataType,
      address: _address,
      customerData,
      msgKey,
      addtlData: item.addtlData,
      activityStatus: item.activityStatus,
    };
  });
};

export const getTariffChangeMessage = (dataType: string): string => {
  if (dataType === DataChangeType.SecureOffer) {
    return 'products.recentOrders.tco_workInProgress';
  }

  return 'products.recentOrders.tc_workInProgress';
};

export const getPaymentAgreementMessage = (dataType: string): string => {
  if (dataType === 'PAYMENT_CALLBACK') {
    return 'products.recentOrders.cba_workInProgress';
  }

  return 'products.recentOrders.pa_workInProgress';
};

export const getInvoiceDataChangeMessage = (dataType: DataChangeType): string => {
  switch (dataType) {
    case DataChangeType.BankAccountChangeOnly:
      return 'products.recentOrders.idcb_workInProgress';
    case DataChangeType.PdfInvoiceChangeOnly:
      return 'products.recentOrders.idce_workInProgress';
    case DataChangeType.InvoiceAddressChangeOnly:
      return 'products.recentOrders.idcr_workInProgress';
    default:
      return 'products.recentOrders.idca_workInProgress';
  }
};

// helper method to get simple product for ui-stuff, do not use it for other purposes
export const getWaermeProductFromVerfuegbarkeit = (v: IProduktVerfuegbarkeit): Product => {
  if (!v) {
    return null;
  }
  const tariffClasses = v?.details?.map(d => d.lieferkomponente) || [];
  return {
    category: Sparte.Fernwaerme,
    tariffClasses,
  } as Product;
};

export function getProductSelectionDetailsFromMappingData(
  data: CommonMappingData,
  priceIndications: PreisIndikation[],
): IProduktAuswahlDetails {
  if (!data || isProductCategoryFernwaerme(data?.categories?.[0])) {
    return null;
  }

  const tariffName = priceIndications?.find(p => p.tarif.ISUTarifKey === data.isuTariffKeys?.[0])?.tarif?.tarifName;
  const tariffNameFormatted = formatTariffName(data.categories?.[0], tariffName);

  return {
    accountId: data.accountNumber,
    anlageId: data.anlageIds?.[0], // only tariff change
    customerNumber: data.customerNumber,
    sparte: data.categories?.[0],
    bedingungen: data.condition,
    tarif: {
      tarifKey: data.tariffKeys?.[0],
      ISUTarifKey: data.isuTariffKeys?.[0],
      tarifOptionen: data.tarifOptionen,
      tarifName: tariffNameFormatted,
      tarifBeschreibung: tariffNameFormatted,
    },
  } as IProduktAuswahlDetails;
}
